/**
 * The settings hub, declared once.
 *
 * 🚨 This file is the ONLY place the settings navigation is described. It feeds
 * the desktop rail, the mobile index, the section routes, the search index and
 * the Skills marketplace cog. Before this existed there were three
 * hand-maintained lists — `TABS` in SettingsShell, `menuItems` + `SECTIONS` in
 * MobileSettings, and `SKILL_SETTINGS_DESTINATIONS` — and they had already
 * drifted. MobileSettings said so in its own comment: "a tab added there is NOT
 * added here — which is how this one shipped desktop-only in #951."
 *
 * The failure is never one wrong implementation, it is that there were three,
 * so `__tests__/registry.test.ts` fails if a surface re-derives the list.
 *
 * ⚠️ No `"use client"` here, and none in anything it imports. A server
 * component and a client component both read this module, and Next replaces
 * every export of a `"use client"` module with a proxy whose body is a throw —
 * so a directive added above would 500 the section routes at render time with
 * nothing in the types to say why (CLAUDE.md anti-pattern #18).
 */

import {
  Building2, Users, ShieldCheck, History, CalendarCog, Tags, Banknote,
  CreditCard, HardHat, MessageSquare, MessagesSquare, Mail, Bell, Plug, Contact,
  UserCircle, Lock, Smartphone, Gift, LifeBuoy, Trash2, Briefcase, Database,
  FileText, ImagePlus, ListChecks, PenLine, Palmtree, ClipboardList, Monitor,
  type LucideIcon, CalendarCheck, Package,
} from 'lucide-react';

import { isTapToPayLaunched } from '@/lib/payments/tap-to-pay-flag';
import { isJoblogicBetaAccount } from '@/lib/joblogic/beta';
// The brand M, drawn to lucide's rules so it sits in a rail of outline icons
// without being the one solid glyph. Directive-free — see the note in that file.
import { MuckaM } from '@/components/ui/MuckaMIcon';

/** Everything a visibility gate is allowed to read. */
export interface SettingsCtx {
  perms: Record<string, boolean>;
  enabledSkillSlugs: string[];
  activeAccountId: string | null;
  isSuperAdmin: boolean;
  workspacesUIEnabled: boolean;
  /** Capacitor. Resolved post-mount on the client; false on the server. */
  native: boolean;
  /** Whether the setup guide applies to this account — already resolved
   *  server-side on `SidebarData`, so reading it here costs nothing. */
  setupGuideEligible?: boolean;
  /** Native binaries that can transact in-app. Pre-IAP builds cannot. */
  iapCapable: boolean;
  /** Row counts for the rail badges. Absent is fine — the badge just hides. */
  counts?: { team?: number; roles?: number; support?: number };
}

/** One control, for the search index. `anchor` scrolls to it on arrival. */
export interface SettingIndexEntry {
  label: string;
  keywords?: string[];
  anchor?: string;
}

export interface SettingsSection {
  /** Globally unique across every group — it is the route segment, the search
   *  key and the legacy `?tab=` key, so it cannot be scoped to its group. */
  id: string;
  label: string | ((c: SettingsCtx) => string);
  icon: LucideIcon;
  /** Shown under the panel title, and as the row's second line on mobile. */
  description: string | ((c: SettingsCtx) => string);
  /** Shorter line for the mobile index, where the full blurb does not fit. */
  mobileDesc?: string | ((c: SettingsCtx) => string);
  /**
   * Liquid Glass phone only: the row's second line on the glass index, saying
   * where the row GOES ("Connect Mucka to your tools") rather than listing what
   * is on it. Its own field so variant A's `mobileDesc` is untouched
   * (docs/glass-batch-4.md, Tom's second round). Falls back to `mobileDesc`.
   */
  glassHint?: string | ((c: SettingsCtx) => string);
  /**
   * Liquid Glass phone only: the row's name where the shared one is ambiguous:
   * the two "Holiday" sections read "Team holiday" and "Your holiday".
   */
  glassLabel?: string;
  /**
   * Liquid Glass phone only: left off the glass index and its search, for a
   * screen that only ever says "Computer only" on a phone (Display). The route
   * still opens; this is a navigation choice, not a permission.
   */
  glassHidden?: boolean;
  /** Permission key required to see it at all. Omit for always-on. */
  permission?: string;
  /** Extra gate beyond the permission — flags, beta accounts, skills. */
  visible?: (c: SettingsCtx) => boolean;
  /** When set, the row leaves the app instead of routing (App Store 3.1.1). */
  externalUrl?: (c: SettingsCtx) => string | null;
  count?: (c: SettingsCtx) => number | undefined;
  settings?: SettingIndexEntry[];
}

export type SettingsGroupId =
  | 'business' | 'team' | 'work' | 'money'
  | 'customers' | 'mucka' | 'connections'
  // Two groups of one. Referrals and Support are single destinations rather
  // than taxonomies, and they sat under You while nobody could find them —
  // "where do I report a problem" is not a question anyone answers with
  // "your profile". A solo group is how the rail promotes a section to a row
  // of its own; the surfaces render one without an accordion or a chip row.
  | 'referrals' | 'support'
  | 'you';

export interface SettingsGroup {
  id: SettingsGroupId;
  label: string;
  icon: LucideIcon;
  /** Draws attention to itself on both surfaces — the offer, not a setting. */
  highlight?: boolean;
  /** Liquid Glass phone only: the one line under a closed group, saying what is in it. */
  glassDesc?: string;
  sections: SettingsSection[];
}

/**
 * 🚨 Payments is two screens behind one section, and always has been. An owner
 * gets Stripe, fees and Direct Debit. Someone who can only take money —
 * `collect_payment` without `manage_workspace` — gets Apple's Tap to Pay on
 * iPhone guide and nothing else, because requirement 4.3 needs education
 * reachable somewhere they can actually go, and the payment sheet is not the
 * place for it. Gated on the launch flag as well: without it that person's
 * screen is empty, and an empty screen is worse than no screen.
 */
export function isEducationOnlyPayments(c: SettingsCtx): boolean {
  return c.perms.manage_workspace !== true
    && c.perms.collect_payment === true
    && isTapToPayLaunched({ isSuperAdmin: c.isSuperAdmin });
}

function billingWebUrl(): string {
  const base = process.env.NEXT_PUBLIC_SITE_URL || 'https://app.mucka.ai';
  return `${base}/settings/mucka/billing`;
}

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    id: 'business',
    label: 'Business',
    glassDesc: 'Your details, branding and paperwork',
    icon: Building2,
    sections: [
      {
        // Only with the multi-workspace flag on (migration 104), and first in
        // the group when it is: which business you are looking at comes before
        // anything about that business.
        id: 'workspaces',
        label: 'Workspaces',
        glassHint: 'Switch business, or add another',
        icon: Building2,
        description: 'Switch between the businesses you belong to, or add another.',
        mobileDesc: 'Switch or add a business',
        visible: c => c.workspacesUIEnabled,
        settings: [
          { label: 'Switch business', keywords: ['change workspace', 'another business', 'second company', 'multiple businesses'] },
          { label: 'Add a business', keywords: ['new workspace', 'create business'] },
        ],
      },
      {
        id: 'details',
        label: 'Details',
        glassHint: 'Name, address, hours, VAT and trade',
        icon: Building2,
        description: 'Your business name, address, VAT and registration numbers, when you are open, and your trade.',
        mobileDesc: 'Name, address, hours and trade',
        permission: 'manage_workspace',
        settings: [
          { label: 'Business name', keywords: ['company name', 'trading name', 'what you are called'], anchor: 'set-business-name' },
          { label: 'Business address', keywords: ['where you are based', 'postcode', 'office address'], anchor: 'set-address' },
          { label: 'Business phone', keywords: ['telephone', 'contact number', 'landline'], anchor: 'set-phone' },
          { label: 'Business email', keywords: ['contact email'], anchor: 'set-email' },
          { label: 'Customer replies go to', keywords: ['where customers reply', 'reply to', 'replies', 'who gets replies', 'reply address'], anchor: 'set-reply-emails' },
          { label: 'Opening hours', keywords: ['when you are open', 'working hours', 'business hours', 'closed', 'out of hours', 'weekends'], anchor: 'set-hours' },
          { label: 'VAT', keywords: ['vat number', 'vat registered', 'tax number'], anchor: 'set-vat' },
          { label: 'Gas Safe number', keywords: ['gas safe register', 'registration number'], anchor: 'set-gas-safe' },
          { label: 'Trade', keywords: ['what you do', 'plumber', 'electrician', 'gas engineer', 'heating'], anchor: 'set-trade' },
          { label: 'Your accountant', keywords: ['accountant', 'bookkeeper', 'accountancy firm', 'who does my books'], anchor: 'set-accountant-firm' },
          { label: "Accountant's email", keywords: ['accountant email', 'send timesheets to accountant'], anchor: 'set-accountant-email' },
        ],
      },
      {
        id: 'branding',
        label: 'Branding',
        glassHint: 'Your logo and colours on paperwork',
        icon: ImagePlus,
        // 🚨 Customer-facing only. These colours paint the quote page, the pay
        // page and the PDFs — never the app.
        description: 'Your logo, how big it prints and where it sits, and the colours your customers see on quotes, invoices and your quote page.',
        mobileDesc: 'Logo and customer-facing colours',
        permission: 'manage_workspace',
        settings: [
          { label: 'Logo', keywords: ['upload logo', 'company logo', 'letterhead'], anchor: 'set-logo' },
          { label: 'Logo usage', keywords: ['how big the logo prints', 'logo size', 'logo position', 'move my logo', 'centre my logo', 'logo in the middle', 'logo on the right'], anchor: 'set-logo-usage' },
          { label: 'Header colour', keywords: ['quote page header'], anchor: 'set-header-colour' },
          { label: 'Primary colour', keywords: ['brand colour', 'customer colours', 'quote colours'], anchor: 'set-primary-colour' },
          { label: 'Secondary colour', keywords: ['accent colour'], anchor: 'set-secondary-colour' },
        ],
      },
      {
        id: 'paperwork',
        label: 'Paperwork',
        glassHint: 'Terms, bank details, footers, numbering',
        icon: FileText,
        description: 'What prints on a quote or an invoice: payment terms, bank details, footers, your accreditation logos, your terms and conditions, and how references are numbered.',
        mobileDesc: 'Terms, bank details, footers, numbering',
        permission: 'manage_workspace',
        settings: [
          { label: 'Payment terms', keywords: ['days to pay', 'due date', '30 days', 'when payment is due'], anchor: 'set-payment-terms' },
          { label: 'Bank details', keywords: ['sort code', 'account number', 'bank transfer', 'where customers pay'], anchor: 'set-bank' },
          { label: 'Quote footer', keywords: ['bottom of a quote'], anchor: 'set-quote-footer' },
          { label: 'Invoice footer', keywords: ['bottom of an invoice'], anchor: 'set-invoice-footer' },
          { label: 'Accreditation logos', keywords: ['accreditation', 'gas safe logo', 'watersafe', 'ciphe', 'oftec', 'which trusted trader', 'trade body', 'badge', 'logo in the footer', 'logo at the bottom'], anchor: 'set-accreditation-logos' },
          { label: 'Quote terms and conditions', keywords: ['terms', 't and cs', 'small print'], anchor: 'set-quote-terms' },
          { label: 'Invoice terms and conditions', keywords: ['terms', 't and cs', 'small print'], anchor: 'set-invoice-terms' },
          { label: 'Invoice numbering', keywords: ['invoice number', 'next invoice', 'prefix', 'reference'], anchor: 'set-invoice-numbering' },
          { label: 'Quote numbering', keywords: ['quote number', 'next quote', 'prefix', 'reference'], anchor: 'set-quote-numbering' },
          { label: 'Client references', keywords: ['their reference', 'customer reference'], anchor: 'set-client-references' },
          { label: 'PO numbers', keywords: ['purchase order'], anchor: 'set-po-numbers' },
          { label: 'How you address customers', keywords: ['title', 'mr', 'mrs', 'miss', 'honorific', 'formal', 'surname', 'mr smith'], anchor: 'set-address-by-title' },
        ],
      },
      {
        id: 'quote-templates',
        label: 'Quote templates',
        glassHint: 'Ready-made quotes to start from',
        icon: FileText,
        description: 'Prefilled quotes for the work you do most, so a common job is a pick rather than a retype.',
        mobileDesc: 'Prefilled quotes for common work',
        permission: 'manage_workspace',
        settings: [
          { label: 'Quote templates', keywords: ['prefilled quote', 'saved quote', 'common job'] },
        ],
      },
      {
        id: 'invoice-templates',
        label: 'Invoice templates',
        glassHint: 'Ready-made invoices to start from',
        icon: FileText,
        description: 'The same for invoices — the lines, the VAT and the terms you use most.',
        mobileDesc: 'Prefilled invoices',
        permission: 'manage_workspace',
        settings: [
          { label: 'Invoice templates', keywords: ['prefilled invoice', 'saved invoice'] },
        ],
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    glassDesc: 'People, roles, holiday and messages',
    icon: Users,
    sections: [
      {
        id: 'team',
        label: 'People',
        glassHint: 'Who is on the team, invites and access',
        icon: Users,
        description: 'Invite teammates, change their roles and see who has access to this workspace.',
        mobileDesc: 'Who has access, and what they hold',
        permission: 'manage_team',
        count: c => c.counts?.team,
        settings: [
          { label: 'Invite someone', keywords: ['add a teammate', 'new engineer', 'add staff'] },
          { label: 'Change someone\'s role', keywords: ['make someone a manager', 'permissions for a person'] },
          { label: 'Remove someone', keywords: ['take access away', 'they have left'] },
        ],
      },
      {
        id: 'roles',
        label: 'Roles',
        glassHint: 'What each role can see and do',
        icon: ShieldCheck,
        description: 'Define what each role can see and do across the app.',
        mobileDesc: 'What each role can see and do',
        permission: 'manage_roles',
        count: c => c.counts?.roles,
        settings: [
          { label: 'What each role can do', keywords: ['permissions', 'access', 'what they can see'] },
          { label: 'Add a custom role', keywords: ['new role'] },
        ],
      },
      {
        // Holiday POLICY and everybody's balances. Under Team rather than
        // Work, because the question it answers is "can I cover July" — a
        // roster question about people, not a question about how the diary
        // promises times.
        // 🚨 `team-holiday`, not `holiday` — section ids are globally unique
        // across every group because the id IS the route segment and the
        // search key, and the You group has its own Holiday screen.
        id: 'team-holiday',
        label: 'Holiday',
        glassHint: 'Allowances, and who has days left',
        glassLabel: 'Team holiday',
        icon: Palmtree,
        description: 'Everyone\u2019s holiday allowance and what they have left, plus when your holiday year starts and whether bank holidays come out of it.',
        mobileDesc: 'Allowances, and who has days left',
        permission: 'manage_team',
        settings: [
          { label: 'Who has days left', keywords: ['holiday', 'annual leave', 'time off', 'days remaining', 'entitlement'] },
          { label: 'When your holiday year starts', keywords: ['holiday year', 'reset', 'april', 'january'], anchor: 'set-holiday-year' },
          { label: 'Default holiday allowance', keywords: ['how many days', 'entitlement', '28 days', '20 days'], anchor: 'set-holiday-default' },
          { label: 'Bank holidays', keywords: ['bank holiday', 'on top', 'included'], anchor: 'set-holiday-bank' },
        ],
      },
      {
        // What Mucka sends the TEAM, decided by the office (Tom, 2026-09-25):
        // a default per role and an override per person. The owner's own
        // messages stay on their Notifications screen — they are the office.
        // 🚨 `team-messaging`, not `messaging` — section ids are unique across
        // every group (the id is the route segment and the search key).
        id: 'team-messaging',
        label: 'Messaging',
        glassHint: 'Job texts, reminders and briefings',
        icon: MessagesSquare,
        description: 'Choose what Mucka sends your team, like job changes, briefings and nudges, for each role or differently for one person.',
        mobileDesc: 'What Mucka sends your team',
        permission: 'manage_team',
        settings: [
          { label: 'What Mucka sends each role', keywords: ['team notifications', 'engineer notifications', 'briefing for engineers', 'stop messaging my engineers', 'subbie texts', 'text my subbie', 'reminders for the team'] },
          { label: 'Set it differently for one person', keywords: ['one engineer', 'turn off for', 'override'] },
        ],
      },
      {
        id: 'activity',
        label: 'Activity',
        glassHint: 'Who changed what, and when',
        icon: History,
        description: 'A log of what happened in this workspace: who created, edited or deleted records, and changes to team roles.',
        permission: 'manage_workspace',
        settings: [
          { label: 'Activity log', keywords: ['audit', 'history', 'who changed it', 'who deleted it'] },
        ],
      },
    ],
  },
  {
    id: 'work',
    label: 'Work',
    glassDesc: 'Job types, statuses, diary and sign-off',
    icon: CalendarCog,
    sections: [
      {
        id: 'job-types',
        label: 'Job types',
        glassHint: 'The jobs you do and how long they take',
        icon: Briefcase,
        description: 'The work this business does, each with how long it usually takes and, where it applies, the certificate it produces.',
        mobileDesc: 'The work you do, and how long it takes',
        permission: 'manage_workspace',
        settings: [
          { label: 'The work you do', keywords: ['type of job', 'service', 'boiler service', 'landlord cert'] },
          { label: 'How long a job takes', keywords: ['duration', 'default length'] },
        ],
      },
      {
        id: 'job-statuses',
        label: 'Job statuses',
        glassHint: 'The steps a job goes through',
        icon: ListChecks,
        description: 'The track a job moves along, and what each step is called here.',
        mobileDesc: 'The steps a job moves through',
        permission: 'manage_workspace',
        settings: [
          { label: 'Job statuses', keywords: ['stages', 'pipeline', 'completed', 'to invoice', 'on site'] },
        ],
      },
      {
        id: 'diary',
        label: 'Diary',
        glassHint: 'How you promise times, travel and days off',
        icon: CalendarCog,
        description: 'How you promise appointment times — exact starts, half days, your own slots or overlapping arrival windows — and therefore when Mucka thinks you are double-booked.',
        mobileDesc: 'How you promise appointment times',
        permission: 'manage_workspace',
        settings: [
          { label: 'How you promise times', keywords: ['arrival window', 'exact start', 'half day', 'am pm', 'slots'], anchor: 'set-diary' },
          { label: 'How long a job usually takes', keywords: ['duration', 'default length', 'how long to allow'], anchor: 'set-diary-length' },
          { label: 'Time off and appointments', keywords: ['approve time off', 'sign off holiday', 'appointment approval'], anchor: 'set-diary-approval' },
          { label: 'Travel time', keywords: ['driving between jobs', 'gap between jobs'], anchor: 'set-diary-travel' },
          { label: 'One-off days', keywords: ['working a saturday', 'weekend', 'keep a day free', 'block a day', 'extra day', 'day off'], anchor: 'set-diary-one-off' },
          { label: 'Access on the diary', keywords: ['key collect', 'keys', 'someone home', 'someone in', 'access', 'tenant', 'who is letting us in'], anchor: 'set-diary-access' },
          { label: 'Double booking', keywords: ['clash', 'overlap', 'conflict'] },
        ],
      },
      {
        id: 'qualifications',
        label: 'Qualifications',
        glassHint: 'Tickets, and which jobs need them',
        icon: ShieldCheck,
        // 🚨 It WARNS and never blocks. An apprentice under supervision is a
        // normal booking; Mucka says what it can see and the owner decides.
        description: 'Which tickets your work needs — Gas Safe, unvented, 18th Edition — and which job types need which. Mucka warns when somebody is put on work they have not got the card for. It never stops you.',
        mobileDesc: 'Which tickets your work needs',
        permission: 'manage_workspace',
        settings: [
          { label: 'Which tickets your work needs', keywords: ['gas safe', 'oftec', 'f gas', 'unvented', '18th edition', 'cards', 'competency'] },
        ],
      },
      {
        id: 'signoff',
        label: 'Sign-off',
        glassHint: 'What customers sign when a job is done',
        icon: PenLine,
        description: 'Whether marking a job complete asks for a customer signature, and the wording they sign under. Your engineers can always take one either way.',
        mobileDesc: 'Whether completing a job asks for a signature',
        permission: 'manage_workspace',
        settings: [
          { label: 'Ask for a signature', keywords: ['customer signature', 'sign off', 'proof of completion'], anchor: 'set-signoff' },
          { label: 'Wording they sign under', keywords: ['signature', 'declaration', 'what the customer agrees to'], anchor: 'set-signoff-statement' },
          { label: 'Which job types need a signature', anchor: 'set-signoff-types' },
        ],
      },
      {
        id: 'job-paperwork',
        label: 'Job paperwork',
        glassHint: 'Job sheets and job reports',
        icon: ClipboardList,
        // ONE screen since 2026-09-04, because it is one skill. Two entries
        // under one skill read as two features, and these are not two
        // features: it is one form at the door with two documents out of it.
        //
        // Same rule as Sign-off above, said the same way: the settings are
        // about being ASKED, not about being allowed. An engineer can start
        // either document on any job with all of this switched off.
        description: "One form your engineer fills in at the door, and up to two documents out of it: the job sheet you keep, and the customer's own copy of what you did.",
        mobileDesc: 'The paperwork your engineer fills in at the door',
        permission: 'manage_workspace',
        // Hidden until the skill is installed, so an account that has never
        // heard of job sheets is not offered settings for them.
        visible: c => c.enabledSkillSlugs.includes('job-sheets'),
        settings: [
          { label: 'What you produce', keywords: ['job sheet', 'job report', 'customer copy', 'paperwork', 'documents'], anchor: 'set-job-documents' },
          { label: 'Start a job sheet on completion', keywords: ['job sheet', 'call slip', 'worksheet'], anchor: 'set-job-sheets' },
          { label: "What's on the sheet", keywords: ['sections', 'parts', 'notes', 'signature', 'attendance', 'photos', 'pictures'], anchor: 'set-job-sheet-sections' },
          { label: 'Which job types get a job sheet', anchor: 'set-job-sheet-types' },
          { label: 'Write up a report on completion', keywords: ['job report', 'customer report', 'what we did', 'send the customer'], anchor: 'set-job-reports' },
          { label: "What's on the report", keywords: ['sections', 'photos', 'suggestions', 'guarantee'], anchor: 'set-job-report-sections' },
          { label: 'Your guarantee', keywords: ['guarantee', 'warranty', 'parts and labour'], anchor: 'set-job-report-warranty' },
          { label: 'Which job types get a report', anchor: 'set-job-report-types' },
        ],
      },
      {
        id: 'parts',
        label: 'Parts',
        glassHint: 'Parts for jobs, and what is on the van',
        icon: Package,
        // One skill with two halves an account can have either or both of
        // (TKT-1542). The install wizard asks; this is where it is changed.
        description: 'Which halves of Parts & Van Stock you use: the parts each job needs and what to pick up each morning, and van stock.',
        mobileDesc: 'Parts for jobs and van stock',
        permission: 'manage_workspace',
        visible: c => c.enabledSkillSlugs.includes('parts-tracker'),
        settings: [
          { label: 'What you keep track of', keywords: ['parts', 'pick up', 'merchant', 'van stock', 'materials'], anchor: 'set-parts-what' },
        ],
      },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    glassDesc: 'Prices, getting paid and tax',
    icon: Banknote,
    sections: [
      {
        id: 'price-list',
        label: 'Price list',
        glassHint: 'Your prices, areas and tiers',
        icon: Tags,
        description: 'Your set prices, so quotes, invoices and the receptionist all quote the same number.',
        mobileDesc: 'Set prices for quotes, invoices & the receptionist',
        permission: 'view_price_list',
        settings: [
          { label: 'Your set prices', keywords: ['rates', 'cost', 'materials', 'labour', 'price'] },
        ],
      },
      {
        // Getting PAID: your customers' money coming in. What you pay Mucka
        // is Subscription, and it is not in this group at all any more — it
        // sits under Mucka, with the thing it pays for. The two were peer rail
        // items called Payments and Billing once, then peer sections under
        // Money; both spellings leaned on the reader to separate two
        // near-synonyms. Money is now one direction of travel only.
        id: 'payments',
        label: 'Getting paid',
        glassHint: c => (isEducationOnlyPayments(c) ? 'Taking a card on your phone' : 'Card payments and Direct Debit'),
        icon: Banknote,
        description: 'How your customers pay you: by card on invoices, and by Direct Debit on service plans.',
        mobileDesc: 'Let customers pay invoices by card',
        visible: c => c.perms.manage_workspace === true || isEducationOnlyPayments(c),
        settings: [
          { label: 'Card payments', keywords: ['stripe', 'sumup', 'take a card', 'pay by card', 'customers paying', 'invoice payments'], anchor: 'set-card-payments' },
          { label: 'Direct Debit', keywords: ['gocardless', 'standing order', 'service plan payments'], anchor: 'set-direct-debit' },
          { label: 'Card fees', keywords: ['transaction fee', 'what stripe charges'], anchor: 'set-card-payments' },
          { label: 'Tap to Pay on iPhone', keywords: ['contactless', 'take payment on the phone'] },
        ],
      },
      {
        id: 'subbie-work',
        label: 'Tax & subcontracting',
        glassHint: 'Your UTR and CIS status',
        icon: HardHat,
        description: 'When you do subcontracted work for another business, this is where you tell Mucka your UTR and CIS status so invoices and payments handle the tax correctly.',
        permission: 'manage_workspace',
        // Was desktop-only, on the argument that it is two fields set once at
        // setup with the HMRC letter in front of you. The argument was about
        // how OFTEN it is opened, which is not the same question as where it
        // can be — and Tom went looking for it on a phone, which settles it.
        // Two short fields render fine on a phone, and search had been
        // offering the screen anyway, so hiding the row only meant it could be
        // found once and never again.
        settings: [
          { label: 'UTR', keywords: ['unique taxpayer reference', 'hmrc'], anchor: 'set-utr' },
          { label: 'CIS status', keywords: ['construction industry scheme', 'subcontractor deduction', '20 percent'], anchor: 'set-cis-status' },
        ],
      },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    glassDesc: 'What your customers get from you',
    icon: MessageSquare,
    sections: [
      {
        id: 'customer-messages',
        label: 'Automatic messages',
        glassHint: 'Texts and emails Mucka sends for you',
        icon: MessageSquare,
        description: 'What Mucka sends your customers without being asked, by text or email, and the wording it uses.',
        mobileDesc: 'What Mucka sends your customers',
        permission: 'manage_workspace',
        // Anchored, so a search result opens the row rather than scrolling to a
        // box in a wall — see SettingsRow's note on `anchor`.
        settings: [
          { label: 'Texts left', keywords: ['buy texts', 'text bundle', 'top up texts', 'sms credits', 'run out of texts', 'auto top up texts', 'free texts'], anchor: 'set-texts-wallet' },
          { label: 'Booking confirmations', keywords: ['confirm the appointment', 'booking confirmation email'], anchor: 'set-booking-confirmations' },
          { label: 'Appointment reminders', keywords: ['reminder text', 'day before', 'reminder email'], anchor: 'set-appointment-reminders' },
          { label: 'On my way', keywords: ['heading over', 'en route', 'on my way text', 'on my way email'], anchor: 'set-on-my-way' },
          { label: 'Copy me in', keywords: ['bcc', 'copy me', 'copied in', 'send me a copy', 'blind copy', 'record of what went out'], anchor: 'set-copy-me-in' },
          { label: 'Text the tenant', keywords: ['rental', 'landlord', 'who gets the texts'], anchor: 'set-tenant-texts' },
          { label: 'Email the tenant', keywords: ['rental', 'arrange access'], anchor: 'set-tenant-emails' },
          { label: 'Review nudge wording', keywords: ['what it says', 'message copy'], anchor: 'set-review-nudge' },
          { label: 'Service due reminder wording', keywords: ['service reminder copy'], anchor: 'set-service-reminder' },
        ],
      },
      {
        id: 'online-booking',
        label: 'Online booking',
        glassHint: 'Your page where customers book in',
        icon: CalendarCheck,
        description: 'Your booking page and website widget: the link, what customers can book, when, where you work, and whether a booking waits for your approval or goes straight in.',
        mobileDesc: 'Booking page, link, service area',
        permission: 'manage_online_booking',
        visible: c => c.enabledSkillSlugs.includes('online-booking'),
        settings: [
          { label: 'Your booking link', keywords: ['booking link', 'booking page', 'book online', 'calendly', 'booking url'], anchor: 'set-booking-link' },
          { label: 'What customers can book', keywords: ['bookable services', 'booking services', 'what they can book'], anchor: 'set-booking-services' },
          { label: 'Where you work', keywords: ['service area', 'how far', 'radius', 'catchment', 'miles'], anchor: 'set-booking-where' },
          { label: 'How bookings land', keywords: ['approve bookings', 'book straight in', 'instant booking', 'ask me first'], anchor: 'set-booking-lands' },
          { label: 'Put it on your website', keywords: ['widget', 'embed', 'website button', 'qr code', 'google business profile'], anchor: 'set-booking-page' },
        ],
      },
      {
        id: 'quote-page',
        label: 'Quote page',
        glassHint: 'What customers see with a quote',
        icon: FileText,
        // Everything but the customer's own note prints BELOW the price. This
        // said "your introduction above the prices" until TKT-1259, which was
        // true until the introduction became "What happens next" and moved
        // under the figure — leaving the one screen that explains these
        // settings describing where they used to print.
        description: 'The branded page a customer opens when you send them a quote: what happens next, who you are and your reviews, all below the price. Switch any of it off.',
        mobileDesc: 'The page your customer opens',
        permission: 'manage_workspace',
        settings: [
          { label: 'What prints on your quote page', keywords: ['turn off', 'hide a section', 'remove about us', 'plain quote', 'sections'], anchor: 'set-quote-sections' },
          { label: 'Quote page header', keywords: ['top of the quote page', 'banner'], anchor: 'set-quote-header' },
          { label: 'What happens next', keywords: ['intro', 'introduction', 'opening message'], anchor: 'set-quote-intro' },
          { label: 'About your business', keywords: ['who you are', 'your story'], anchor: 'set-quote-about' },
          { label: 'Examples of your work', keywords: ['gallery', 'photos of your work', 'previous jobs'], anchor: 'set-quote-gallery' },
          { label: 'Reviews on your quote page', keywords: ['testimonials', 'show reviews', 'type in a review', 'not on google'], anchor: 'set-quote-reviews' },
        ],
      },
      {
        id: 'email',
        label: 'Email',
        glassHint: 'Sending address, signature and templates',
        icon: Mail,
        description: 'Your sending address, signature, message templates and review request copy.',
        mobileDesc: 'Signature & message templates',
        permission: 'manage_workspace',
        settings: [
          { label: 'Sending address', keywords: ['from address', 'email address', 'sender address', 'mail.mucka.ai', 'what address do emails come from', 'handle'], anchor: 'set-sending-address' },
          { label: 'Email signature', keywords: ['sign off', 'sender name', 'from name'] },
          { label: 'Message templates', keywords: ['saved email', 'canned reply'] },
          { label: 'Review request wording', keywords: ['asking for a review'] },
        ],
      },
    ],
  },
  {
    id: 'mucka',
    label: 'Mucka',
    glassDesc: 'Voice, your plan and notifications',
    icon: MuckaM,
    sections: [
      {
        id: 'your-mucka',
        label: 'Voice & personality',
        glassHint: 'How Mucka sounds and behaves',
        icon: MuckaM,
        description: "Pick your assistant's voice and tweak how it behaves.",
        mobileDesc: 'Voice, accent and name',
        settings: [
          // 🚨 The voice is the one setting on this page with NO anchor, and
          // that is not an oversight. Everything else here is a `SettingsRow`
          // that opens itself on a hash match; the accent map is deliberately
          // not a row (you cannot read a voice off a collapsed line), so it has
          // no id to open. Landing on the section is what search does for it,
          // and the map is the first thing on the screen anyway.
          { label: 'Mucka\'s voice', keywords: ['accent', 'how it sounds', 'voice gender'] },
          { label: 'What Mucka calls you', anchor: 'set-mucka-name', keywords: ['your name', 'their name'] },
          {
            label: 'How Mucka talks to you',
            anchor: 'set-mucka-personality',
            keywords: ['personality', 'tone of voice', 'humour', 'cheeky', 'formal'],
          },
          {
            label: 'How Mucka writes to your customers',
            anchor: 'set-customer-tone',
            keywords: ['customer tone', 'friendly', 'professional', 'i or we', 'reply voice', 'house style', 'sound professional', 'how we write to customers', 'sign off'],
          },
          // Searched for in the words people actually use — "hands free" and
          // "listening" far more often than "wake word", and "is it recording
          // me" is the question this setting exists to answer.
          {
            label: 'Wake word',
            anchor: 'set-wake-word',
            keywords: [
              'alright mucka', 'hands free', 'voice activation', 'say its name',
              'listening', 'always listening', 'is it recording me', 'microphone',
            ],
          },
        ],
      },
      {
        // What you pay MUCKA, so it lives with Mucka — not in Money, which is
        // your customers' money coming in. Two sections called Getting paid
        // and Subscription under one Money heading still leaned on the reader
        // to tell two near-synonyms apart at a glance; under Mucka the
        // question "is this where my customers' money lands?" cannot even be
        // asked.
        id: 'billing',
        label: 'Subscription',
        glassHint: 'Your plan and what you pay',
        icon: CreditCard,
        description: 'What you pay Mucka: your plan, your invoices and your payment method.',
        mobileDesc: 'Plan & payment details',
        permission: 'manage_billing',
        // Pre-IAP native binaries cannot show a purchase or manage CTA
        // (App Store 3.1.1 / 3.1.3(b)), so the row links out to the web
        // instead of routing. IAP-capable builds and the web keep the
        // inline section, where BillingTab renders the compliant surface.
        externalUrl: c => (c.native && !c.iapCapable ? billingWebUrl() : null),
        settings: [
          { label: 'Your plan', keywords: ['subscription', 'what you pay mucka', 'upgrade', 'downgrade'] },
          { label: 'Your payment method', keywords: ['card on file', 'change card'] },
          { label: 'Your Mucka invoices', keywords: ['receipts', 'billing history'] },
          { label: 'Cancel', keywords: ['stop paying', 'end subscription'] },
        ],
      },
      {
        // Personal prefs, but the question they answer is "when does Mucka
        // talk to me" — which is about the assistant, not about your login.
        id: 'notifications',
        label: 'Notifications',
        glassHint: 'What Mucka tells you, and when',
        icon: Bell,
        description: 'Choose what gets sent to whom and when — channels, briefings, nudges and quiet hours.',
        mobileDesc: 'Briefings, push events, quiet hours',
        settings: [
          { label: 'Quiet hours', keywords: ['do not disturb', 'evening', 'night', 'stop texting me', 'late'] },
          { label: 'Morning briefing', keywords: ['daily summary', 'what is on today'] },
          { label: 'End-of-day summary', keywords: ['evening recap'] },
          { label: 'Push notifications', keywords: ['alerts on my phone'] },
          { label: 'WhatsApp from Mucka', keywords: ['message me on whatsapp'] },
          { label: 'Event alerts', keywords: ['payment received', 'review received', 'job updates'] },
        ],
      },
    ],
  },
  {
    id: 'connections',
    label: 'Connections',
    glassDesc: 'Your other tools, and bringing data in',
    icon: Plug,
    sections: [
      {
        id: 'integrations',
        label: 'Integrations',
        glassHint: 'Connect Mucka to your tools',
        icon: Plug,
        description: 'Connect Mucka to the tools your team already uses — WhatsApp, your calendar, accounting and more.',
        mobileDesc: 'WhatsApp, calendar, accounting',
        settings: [
          { label: 'WhatsApp', keywords: ['pair my phone', 'message through whatsapp'] },
          { label: 'Xero', keywords: ['accounting', 'push invoices'] },
          { label: 'QuickBooks', keywords: ['qbo', 'accounting'] },
          { label: 'FreeAgent', keywords: ['accounting'] },
          { label: 'Sage', keywords: ['accounting'] },
          { label: 'Google Calendar', keywords: ['sync my diary', 'calendar'] },
          { label: 'Microsoft 365', keywords: ['outlook', 'calendar'] },
          { label: 'Zapier', keywords: ['automation'] },
        ],
      },
      {
        id: 'crm',
        label: 'CRM',
        glassHint: 'Mirror JobLogic into Mucka',
        icon: Contact,
        description: 'Mirror your CRM into Mucka. Connect JobLogic to pull your customers, sites, jobs and engineers across — JobLogic stays your source of truth.',
        mobileDesc: 'Mirror JobLogic into Mucka',
        // Pilot accounts only; manage_billing mirrors the connect permission.
        visible: c => isJoblogicBetaAccount(c.activeAccountId) && c.perms.manage_billing === true,
        settings: [
          { label: 'JobLogic', keywords: ['crm', 'mirror', 'field service'] },
        ],
      },
      {
        id: 'import',
        label: 'Import data',
        glassHint: 'Bring in customers and jobs',
        icon: Database,
        // The routes themselves are the page's first question ("Where is your
        // data now?"), so this only says what the page is for.
        description: 'Bring your customers and jobs into Mucka from wherever they are now.',
        mobileDesc: 'Bring your data across',
        permission: 'manage_workspace',
        settings: [
          { label: 'Import clients and jobs', keywords: ['csv', 'spreadsheet', 'excel', 'google sheet', 'bring my data across'] },
          { label: 'Bring customers in from your phone', keywords: ['phone contacts', 'address book', 'contacts', 'iphone', 'android', 'sync contacts', 'two way sync', 'caller id'] },
          { label: 'Ask your old provider for your data', keywords: ['migration', 'migrate', 'switch', 'moving from', 'export', 'tradify', 'commusoft', 'servicem8', 'joblogic', 'payaca', 'gas app', 'old system'] },
        ],
      },
    ],
  },
  {
    // The other group of one — see Referrals above for why they are not
    // sections of You any more. This one matters more: it holds the sole
    // "report a problem" button in the app, and somebody with a problem is
    // not in a mood to go looking under a heading called You.
    id: 'support',
    label: 'Support',
    glassDesc: 'Report a problem, see your tickets',
    icon: LifeBuoy,
    sections: [
      {
        // Always visible, even with no tickets. Gating it on having one meant
        // the only route to support was already having used support.
        id: 'support',
        label: 'Support',
        glassHint: 'Report a problem, see your tickets',
        icon: LifeBuoy,
        description: 'Conversations with the Mucka support team.',
        mobileDesc: 'Report a problem or check a ticket',
        count: c => c.counts?.support,
        settings: [
          { label: 'Report a problem', keywords: ['contact support', 'raise a ticket', 'something is broken', 'help'] },
        ],
      },
    ],
  },
  {
    id: 'you',
    label: 'You',
    glassDesc: 'Your profile, holiday, security and this phone',
    icon: UserCircle,
    sections: [
      {
        id: 'profile',
        label: 'Profile',
        glassHint: 'Your name, photo and WhatsApp',
        icon: UserCircle,
        description: 'Your name, contact details and avatar across the app.',
        mobileDesc: 'Your account details',
        settings: [
          { label: 'Your name', keywords: ['what you are called'], anchor: 'set-profile-name' },
          { label: 'Your email', keywords: ['login email', 'sign in email'], anchor: 'set-profile-email' },
          { label: 'Your phone', keywords: ['your mobile'], anchor: 'set-profile-phone' },
          { label: 'Your photo', keywords: ['avatar', 'profile picture'], anchor: 'set-profile-photo' },
          { label: 'Where your day starts', keywords: ['home postcode', 'travel time', 'first job of the day', 'drive from home'], anchor: 'set-profile-home-postcode' },
        ],
      },
      {
        // No permission key. It is the reader's own entitlement, and there is
        // no seat in the product that should not be able to see how many days
        // off it has — a subbie included, whose allowance is simply zero.
        id: 'holiday',
        label: 'Holiday',
        glassHint: 'Book time off, and see what is left',
        glassLabel: 'Your holiday',
        icon: Palmtree,
        description: 'Your holiday allowance, what you have taken and what is left \u2014 and where you book time off.',
        mobileDesc: 'Your allowance, and book time off',
        settings: [
          { label: 'Days you have left', keywords: ['holiday', 'annual leave', 'time off', 'how many days', 'allowance', 'entitlement'] },
          { label: 'Book holiday', keywords: ['book time off', 'request holiday', 'ask for a week off', 'annual leave'] },
        ],
      },
      {
        id: 'security',
        label: 'Security',
        glassHint: 'Password and two-step sign-in',
        icon: Lock,
        description: 'Password, two-factor authentication and active sessions.',
        mobileDesc: 'Password, 2FA, sessions',
        settings: [
          { label: 'Change password', keywords: ['new password', 'reset password'] },
          { label: 'Two-factor authentication', keywords: ['2fa', 'authenticator', 'extra security'] },
          { label: 'Active sessions', keywords: ['where i am logged in', 'sign out everywhere'] },
        ],
      },
      {
        // Was mobile-only. It renders a "Mobile app only" state off-native,
        // so the desktop rail can carry it rather than hiding a screen that
        // exists — half the drift this registry closes was of that shape.
        id: 'device',
        label: 'Device',
        // On a glass phone the device IS the phone in your hand (prototype v36).
        glassLabel: 'This phone',
        glassHint: 'Notifications, app lock and directions',
        icon: Smartphone,
        description: 'Push notifications, haptics, directions and app lock on this device.',
        mobileDesc: 'Notifications, haptics, biometrics',
        settings: [
          { label: 'Push notifications on this device', keywords: ['turn alerts on'] },
          { label: 'Notifications from each business', keywords: ['other business', 'second business', 'workspace notifications', 'mute a business'] },
          { label: 'Haptics', keywords: ['vibration', 'buzz'] },
          { label: 'App lock', keywords: ['face id', 'fingerprint', 'biometrics', 'passcode'] },
          { label: 'Directions', keywords: ['maps', 'navigation app'] },
        ],
      },
      {
        // How Mucka looks on this computer. Called Display because that is
        // the word on every phone, telly and laptop for "how the screen
        // looks" — "Interface" and "UI" are ours, not theirs. Per computer,
        // like Device, so it reads the browser's storage rather than a row.
        id: 'display',
        label: 'Display',
        glassHidden: true,
        icon: Monitor,
        description: 'How Mucka looks on this computer: which page it opens on, and whether scroll bars show.',
        mobileDesc: 'Start page and scroll bars on your computer',
        settings: [
          { label: 'Start page', keywords: ['home page', 'opens on', 'first page', 'landing page', 'open on quotes', 'open on actions', 'default page'] },
          { label: 'Show scroll bars', keywords: ['scrollbar', 'scroll bar', 'scrollbars', 'cant scroll', 'hidden scroll', 'see more', 'drag down the page'] },
        ],
      },
      {
        id: 'account',
        label: 'Account',
        glassHint: 'Leave the business, or delete it',
        icon: Trash2,
        description: 'Delete this business, leave it, or close your Mucka account.',
        mobileDesc: 'Delete business, leave, or close account',
        settings: [
          { label: 'Delete this business', keywords: ['remove workspace'] },
          { label: 'Leave this business', keywords: ['remove myself'] },
          { label: 'Close your Mucka account', keywords: ['delete my account'] },
        ],
      },
    ],
  },
  {
    /**
     * A group of one, and deliberately so.
     *
     * It lived inside You, where an owner looking to share Mucka had to guess
     * that "You" was the heading covering it — and the only people who ever
     * found it were the ones already looking for it. The rail is the only
     * surface anyone browses, so a thing nobody thinks to look for has to be
     * ON the rail or it does not exist. A solo group is exactly that: the row
     * IS the destination, and the surfaces skip the chip row and the mobile
     * accordion when a group holds one section.
     *
     * LAST in the array and `highlight: true`, on both surfaces. Everything
     * above it is a setting somebody came here to change; this is the one row
     * that asks something OF the owner, so it reads better as a footer offer
     * than as the eighth heading in a list of admin. Standing out is what
     * earns it the bottom slot — a plain row down there is a row nobody
     * reaches.
     */
    id: 'referrals',
    highlight: true,
    label: 'Referrals',
    glassDesc: 'Share Mucka, earn 10%',
    icon: Gift,
    sections: [
      {
        id: 'referrals',
        label: 'Referrals',
        glassHint: 'Your code, and what you have made',
        icon: Gift,
        description: 'Share Mucka and earn 10% of what your mates pay for 12 months.',
        mobileDesc: 'Share Mucka, earn 10%',
        settings: [
          { label: 'Your referral code', keywords: ['refer a friend', 'share mucka', 'share and earn', 'earn'] },
        ],
      },
    ],
  },
];

/* ─────────────────────────── Resolving ─────────────────────────── */

export interface ResolvedSection {
  id: string;
  groupId: SettingsGroupId;
  groupLabel: string;
  label: string;
  description: string;
  mobileDesc: string;
  /** Liquid Glass phone: the row's name and its "where it goes" line. */
  glassLabel: string;
  glassHint: string;
  /** Liquid Glass phone: not listed on the glass index or in its search. */
  glassHidden: boolean;
  icon: LucideIcon;
  href: string;
  externalUrl: string | null;
  count?: number;
}

export interface ResolvedGroup {
  id: SettingsGroupId;
  label: string;
  icon: LucideIcon;
  highlight?: boolean;
  /** Liquid Glass phone: what is in the group, under its closed heading. */
  glassDesc: string;
  sections: ResolvedSection[];
}

function text(
  v: string | ((c: SettingsCtx) => string) | undefined,
  c: SettingsCtx,
  fallback: string,
): string {
  if (v === undefined) return fallback;
  return typeof v === 'function' ? v(c) : v;
}

export function sectionHref(groupId: SettingsGroupId, sectionId: string): string {
  return `/settings/${groupId}/${sectionId}`;
}

/**
 * Whether this viewer may see this section. There is no second answer.
 *
 * 🚨 A section used to be able to declare `surfaces: ['desktop']` and vanish
 * from the phone. That mechanism is GONE, deliberately, and this is the note
 * explaining why it should not come back.
 *
 * It was only ever used twice, and both were judgements about how OFTEN a
 * screen gets opened — Activity is "a long audit list you read at a desk",
 * Tax & subcontracting was "two fields set once with the HMRC letter in front
 * of you". Both are reasonable statements about frequency and neither is a
 * reason the screen cannot EXIST on a phone. What they actually produced was a
 * setting an owner could reach by search, use, and then never find again by
 * looking — which is how Tom hit it.
 *
 * `visibleSections` and the section ROUTE now share this one function, so a
 * row that is offered is a page that opens and a page that opens is a row that
 * is offered. `settings-nav-parity` fails if the two surfaces ever differ
 * again, which they now cannot without deleting this.
 */
function isVisible(s: SettingsSection, c: SettingsCtx): boolean {
  if (s.visible) return s.visible(c);
  if (s.permission) return c.perms[s.permission] === true;
  return true;
}

/** Kept as a name because the ROUTE asks a question that reads differently —
 *  "may this person open this URL" — even though the answer is now the same. */
const isPermitted = isVisible;

function resolveSection(s: SettingsSection, g: SettingsGroup, c: SettingsCtx): ResolvedSection {
  const label = text(s.label, c, s.id);
  const mobileDesc = text(s.mobileDesc, c, text(s.description, c, ''));
  return {
    id: s.id,
    groupId: g.id,
    groupLabel: g.label,
    label,
    description: text(s.description, c, ''),
    mobileDesc,
    glassLabel: s.glassLabel ?? label,
    glassHint: text(s.glassHint, c, mobileDesc),
    glassHidden: s.glassHidden === true,
    icon: s.icon,
    href: sectionHref(g.id, s.id),
    externalUrl: s.externalUrl?.(c) ?? null,
    // `|| undefined` so a zero never renders: "Support 0" is a badge saying
    // there is nothing to badge. The chip shows a count or it shows nothing.
    count: s.count?.(c) || undefined,
  };
}

/** The groups this person can see, with empty groups dropped. */
export function visibleGroups(c: SettingsCtx): ResolvedGroup[] {
  return SETTINGS_GROUPS
    .map(g => ({
      id: g.id,
      label: g.label,
      icon: g.icon,
      highlight: g.highlight,
      glassDesc: g.glassDesc ?? '',
      sections: g.sections.filter(s => isVisible(s, c)).map(s => resolveSection(s, g, c)),
    }))
    .filter(g => g.sections.length > 0);
}

/** Every visible section, flattened in rail order. */
export function visibleSections(c: SettingsCtx): ResolvedSection[] {
  return visibleGroups(c).flatMap(g => g.sections);
}

/** One section by id, or null when it does not exist or is not theirs to see. */
export function findVisibleSection(id: string, c: SettingsCtx): ResolvedSection | null {
  for (const g of SETTINGS_GROUPS) {
    const s = g.sections.find(x => x.id === id);
    if (s) return isPermitted(s, c) ? resolveSection(s, g, c) : null;
  }
  return null;
}

/** Where an empty `/settings/<group>` should land. */
export function firstSectionOfGroup(groupId: string, c: SettingsCtx): ResolvedSection | null {
  return visibleGroups(c).find(g => g.id === groupId)?.sections[0] ?? null;
}

/** Where a bare `/settings` should land when the board has nothing to show. */
export function firstVisibleSection(c: SettingsCtx): ResolvedSection | null {
  return visibleSections(c)[0] ?? null;
}

/** Every section id declared, visible or not — for tests and the legacy map. */
export const ALL_SECTION_IDS: string[] =
  SETTINGS_GROUPS.flatMap(g => g.sections.map(s => s.id));

/** The group a section id belongs to, ignoring visibility. Used by the legacy
 *  redirect, which has to answer before it knows who is asking. */
export function groupIdOfSection(sectionId: string): SettingsGroupId | null {
  for (const g of SETTINGS_GROUPS) {
    if (g.sections.some(s => s.id === sectionId)) return g.id;
  }
  return null;
}

/* ───────────────────────── Legacy `?tab=` ───────────────────────── */

/**
 * Every id the old single-page shell accepted, mapped to its section.
 *
 * Section ids were deliberately kept equal to the old tab ids, so this is
 * mostly the identity — which is the point: a rename here is a dead link
 * somewhere in the app, and the identity mapping means the only entries that
 * need thought are the ones that genuinely moved.
 *
 * `?sub=` is left alone: IntegrationsTab reads it itself and still does.
 */
export const LEGACY_TAB_IDS: Record<string, string> = {
  // Landed on the whole tab, which is now six screens. Details is the one
  // an owner following an old link was almost always after.
  workspaces: 'details',
  paperwork: 'paperwork',
  diary: 'diary',
  team: 'team',
  roles: 'roles',
  activity: 'activity',
  billing: 'billing',
  payments: 'payments',
  email: 'email',
  'customer-messages': 'customer-messages',
  integrations: 'integrations',
  crm: 'crm',
  'subbie-work': 'subbie-work',
  'price-list': 'price-list',
  notifications: 'notifications',
  referrals: 'referrals',
  'your-mucka': 'your-mucka',
  profile: 'profile',
  security: 'security',
  device: 'device',
  account: 'account',
  support: 'support',
  // Retired before this registry existed: ?tab=whatsapp was normalised to the
  // integrations tab with ?sub=whatsapp, and the same normalisation still runs
  // in the redirect. Kept here so the old link resolves rather than 404s.
  whatsapp: 'integrations',
};

/** The path a legacy `?tab=` value should redirect to, or null if unknown. */
export function legacyTabPath(tab: string | null | undefined): string | null {
  if (!tab) return null;
  const sectionId = LEGACY_TAB_IDS[tab];
  if (!sectionId) return null;
  const groupId = groupIdOfSection(sectionId);
  return groupId ? sectionHref(groupId, sectionId) : null;
}
